import {
  type FunctionComponent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Button, Modal } from '@dr.pogodin/react-utils';

import Throbber from 'components/Throbber';

import { fetchVimeoLinks, pickSource } from 'services/vimeoVideoData';

import { type ArtistT, isVimeoUrl } from 'utils';

import IconCross from 'assets/images/icon-cross-24px.svg';
import IconStarGold from 'assets/images/icon-start-gold-24px.svg';
import IconStarGray from 'assets/images/icon-start-gray-24px.svg';

import iconButtonTheme from 'themes/Button/icon-48px.scss';

import videoModalTheme from './video-modal.scss';

import './style.scss';

type PropsT = {
  artist: ArtistT;
  onClose: () => void;
};

export const PractitionerIntroVideoModal: FunctionComponent<PropsT> = ({
  artist,
  onClose,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [src, setSrc] = useState<null | string>(null);
  const [videoIsReady, setVideoIsReady] = useState(false);
  const [needsTapForSound, setNeedsTapForSound] = useState(false);
  const [failed, setFailed] = useState(false);

  const artistRanking: ReactNode[] = [];
  if (artist.rating) {
    for (let i = 0; i < artist.rating; ++i) {
      artistRanking.push(<IconStarGold key={`star/${i}`} />);
    }
    for (let i = artist.rating; i < 5; ++i) {
      artistRanking.push(<IconStarGray key={`star/${i}`} />);
    }
  }

  const introUrl = artist.videoUrl;

  // Resolve the native streaming source for the intro clip.
  useEffect(() => {
    if (!introUrl) return undefined;
    if (!isVimeoUrl(introUrl)) throw Error('Not a Vimeo URL');

    const live = { current: true };

    void (async () => {
      const links = await fetchVimeoLinks(introUrl).catch(() => null);
      const picked = links ? pickSource(links) : null;
      if (!live.current) return;
      if (picked) setSrc(picked);
      else setFailed(true);
    })();

    return () => {
      live.current = false;
    };
  }, [introUrl]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !src) return undefined;

    const live = { current: true };
    v.src = src;
    v.muted = false;

    void v.play().then(
      () => {
        if (live.current) setNeedsTapForSound(videoRef.current?.muted ?? false);
      },
      () => {
        const el = videoRef.current;
        if (!live.current || !el) return;
        el.muted = true;
        setNeedsTapForSound(true);
        void el.play().catch(() => {
          if (live.current) setFailed(true);
        });
      },
    );

    return () => {
      live.current = false;
      v.pause();
    };
  }, [src]);

  const enableSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    setNeedsTapForSound(false);
    void v.play().catch(() => undefined);
  };

  return (
    <Modal
      onCancel={onClose}
      theme={videoModalTheme}
    >
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        onEnded={onClose}
        onError={() => {
          setFailed(true);
        }}
        onPlaying={() => {
          setVideoIsReady(true);
        }}
        playsInline
        preload="auto"
        ref={videoRef}
        styleName="video"
      />
      {videoIsReady || failed ? null : <Throbber />}
      {failed ? (
        <div styleName="error">
          Sorry, this video can&apos;t be played right now.
        </div>
      ) : null}
      {needsTapForSound ? (
        <button
          onClick={enableSound}
          styleName="soundButton"
          type="button"
        >
          Tap for sound
        </button>
      ) : null}
      <div styleName="controls">
        <div styleName="nameAndRating">
          <div styleName="artistName">{artist.displayName ?? `${artist.user.firstName} ${artist.user.lastName}`}</div>
          <div styleName="starRating">{artistRanking}</div>
        </div>
        <Button onClick={onClose} theme={iconButtonTheme}>
          <IconCross />
        </Button>
      </div>
    </Modal>
  );
};
